import { useCallback, useEffect, useRef, useState } from "react";

interface ResourceState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<T | null>;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export function useResource<T>(
  loader: () => Promise<T>,
  dependencies: React.DependencyList = [],
  refreshInterval = 0,
): ResourceState<T> {
  const loaderRef = useRef(loader);
  const sequence = useRef(0);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  loaderRef.current = loader;

  const refresh = useCallback(async () => {
    const requestSequence = ++sequence.current;
    setError(null);
    setData((current) => {
      if (current === null) setLoading(true);
      else setRefreshing(true);
      return current;
    });
    try {
      const result = await loaderRef.current();
      if (sequence.current === requestSequence) setData(result);
      return result;
    } catch (caught) {
      const nextError =
        caught instanceof Error ? caught : new Error("数据读取失败");
      if (sequence.current === requestSequence) setError(nextError);
      return null;
    } finally {
      if (sequence.current === requestSequence) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    return () => {
      sequence.current += 1;
    };
    // Dependencies intentionally control when the resource is reloaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, ...dependencies]);

  useEffect(() => {
    if (!refreshInterval) return undefined;
    const timer = window.setInterval(() => void refresh(), refreshInterval);
    return () => window.clearInterval(timer);
  }, [refresh, refreshInterval]);

  return { data, error, loading, refreshing, refresh, setData };
}
